import React, { useState, useMemo } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Link,
  Button,
  Collapse,
  Chip,
  ToggleButtonGroup,
  ToggleButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Badge
} from '@mui/material';
import {
  Close as CloseIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  Error as ErrorIcon,
  Download as DownloadIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  HourglassEmpty as HourglassEmptyIcon,
  FilterList as FilterListIcon,
  Code as CodeIcon,
  FolderOpen as FolderOpenIcon
} from '@mui/icons-material';

/**
 * 배치 변환 결과 표시 Drawer
 */
const BatchResultsDrawer = ({
  open,
  onClose,
  results = []
}) => {
  // 각 항목별 검증 오류 확장 상태 관리
  const [expandedErrors, setExpandedErrors] = useState({});
  // Drawer 높이 상태 (기본 85vh)
  const [drawerHeight, setDrawerHeight] = useState(85);
  const [isResizing, setIsResizing] = useState(false);
  // 상태 필터 (all, success, error, queued)
  const [statusFilter, setStatusFilter] = useState('all');
  // 요청 바디 표시 여부
  const [showRequestBody, setShowRequestBody] = useState({});
  // 지점 그룹 확장 상태
  const [expandedGroups, setExpandedGroups] = useState({});
  // 프롬프트 그룹 확장 상태 (2단계)
  const [expandedPrompts, setExpandedPrompts] = useState({});

  // 드래그로 높이 조절
  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsResizing(true);
  };

  const handleMouseMove = (e) => {
    if (!isResizing) return;
    const windowHeight = window.innerHeight;
    const newHeight = ((windowHeight - e.clientY) / windowHeight) * 100;
    // 30% ~ 95% 범위로 제한
    setDrawerHeight(Math.min(95, Math.max(30, newHeight)));
  };

  const handleMouseUp = () => {
    setIsResizing(false);
  };

  // 마우스 이벤트 리스너 등록
  React.useEffect(() => {
    if (isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  const toggleErrorExpand = (index) => {
    setExpandedErrors(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const toggleRequestBody = (index) => {
    setShowRequestBody(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const toggleGroup = (groupKey) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupKey]: !prev[groupKey]
    }));
  };

  const togglePrompt = (branchName, promptId) => {
    const key = `${branchName}:${promptId}`;
    setExpandedPrompts(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // 상태별 카운트 (단순화)
  const successCount = results.filter(r => r.status === 'success' || r.status === 'validation_failed').length;
  const errorCount = results.filter(r => r.status === 'error').length;
  const queuedCount = results.filter(r => r.status === 'queued').length;

  // 파일명에서 지점명 추출 (그룹화 키)
  const extractBranchName = (fileName) => {
    // 패턴: [트리니티_용산점], (트리니티_반포점), 5 트리니티_청담점] 등
    const patterns = [
      /[\[（\(]([^)\]）]+점)[\]）\)]/,
      /[\[（\(]([^)\]）]+)[\]）\)]/,
      /(\d+\s*[^_]+점)/,
    ];
    for (const pattern of patterns) {
      const match = fileName?.match(pattern);
      if (match) return match[1].trim();
    }
    return '기타';
  };

  // 결과를 지점별 → 프롬프트별로 2단계 그룹화
  const groupedResults = useMemo(() => {
    const groups = {};
    results.forEach((item, originalIndex) => {
      const branchName = extractBranchName(item.fileName);
      const promptId = item.promptId || '프롬프트 없음';

      if (!groups[branchName]) {
        groups[branchName] = {};
      }
      if (!groups[branchName][promptId]) {
        groups[branchName][promptId] = [];
      }
      groups[branchName][promptId].push({ ...item, originalIndex });
    });

    // 정렬: 오류가 있는 그룹을 먼저
    return Object.entries(groups).sort((a, b) => {
      const aHasError = Object.values(a[1]).flat().some(r => r.status === 'error');
      const bHasError = Object.values(b[1]).flat().some(r => r.status === 'error');
      if (aHasError && !bHasError) return -1;
      if (!aHasError && bHasError) return 1;
      return a[0].localeCompare(b[0]);
    });
  }, [results]);

  // 필터링된 결과
  const filteredResults = useMemo(() => {
    if (statusFilter === 'all') return results;
    if (statusFilter === 'success') return results.filter(r => r.status === 'success' || r.status === 'validation_failed');
    if (statusFilter === 'error') return results.filter(r => r.status === 'error');
    if (statusFilter === 'queued') return results.filter(r => r.status === 'queued');
    return results;
  }, [results, statusFilter]);

  // 초기 그룹 확장 상태 설정 (오류가 있는 그룹은 기본 확장)
  React.useEffect(() => {
    if (open && results.length > 0) {
      const initialExpanded = {};
      const initialPromptExpanded = {};

      groupedResults.forEach(([branchName, promptGroups]) => {
        const allItems = Object.values(promptGroups).flat();
        const hasError = allItems.some(r => r.status === 'error');
        initialExpanded[branchName] = hasError || groupedResults.length <= 3;

        // 프롬프트 그룹도 오류가 있으면 확장
        Object.entries(promptGroups).forEach(([promptId, items]) => {
          const promptKey = `${branchName}:${promptId}`;
          const promptHasError = items.some(r => r.status === 'error');
          initialPromptExpanded[promptKey] = promptHasError;
        });
      });

      setExpandedGroups(initialExpanded);
      setExpandedPrompts(initialPromptExpanded);
    }
  }, [open, results.length]);

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          height: `${drawerHeight}vh`,
          p: 3,
          transition: isResizing ? 'none' : 'height 0.1s ease-out'
        }
      }}
    >
      {/* 리사이즈 핸들 */}
      <Box
        onMouseDown={handleMouseDown}
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '12px',
          cursor: 'ns-resize',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: isResizing ? 'action.hover' : 'transparent',
          '&:hover': { bgcolor: 'action.hover' },
          zIndex: 1
        }}
      >
        <Box sx={{
          width: '50px',
          height: '4px',
          bgcolor: 'grey.400',
          borderRadius: '2px'
        }} />
      </Box>
      <Box sx={{ mt: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            배치 변환 결과 <Typography component="span" variant="caption" color="text.secondary">(상단 바를 드래그하여 높이 조절)</Typography>
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        {/* 배치 결과 요약 */}
        {results.length > 0 && (
          <Box sx={{ mb: 3 }}>
            {/* 상태 요약 카드 */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
              <Paper
                elevation={statusFilter === 'all' ? 3 : 1}
                onClick={() => setStatusFilter('all')}
                sx={{
                  px: 2, py: 1, cursor: 'pointer',
                  border: statusFilter === 'all' ? '2px solid' : '1px solid',
                  borderColor: statusFilter === 'all' ? 'primary.main' : 'divider',
                  '&:hover': { bgcolor: 'action.hover' }
                }}
              >
                <Typography variant="body2" color="text.secondary">전체</Typography>
                <Typography variant="h5" fontWeight="bold">{results.length}</Typography>
              </Paper>
              {queuedCount > 0 && (
                <Paper
                  elevation={statusFilter === 'queued' ? 3 : 1}
                  onClick={() => setStatusFilter('queued')}
                  sx={{
                    px: 2, py: 1, cursor: 'pointer',
                    border: statusFilter === 'queued' ? '2px solid' : '1px solid',
                    borderColor: statusFilter === 'queued' ? 'info.main' : 'divider',
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                >
                  <Typography variant="body2" color="info.main">대기중</Typography>
                  <Typography variant="h5" fontWeight="bold" color="info.main">{queuedCount}</Typography>
                </Paper>
              )}
              <Paper
                elevation={statusFilter === 'success' ? 3 : 1}
                onClick={() => setStatusFilter('success')}
                sx={{
                  px: 2, py: 1, cursor: 'pointer',
                  border: statusFilter === 'success' ? '2px solid' : '1px solid',
                  borderColor: statusFilter === 'success' ? 'success.main' : 'divider',
                  '&:hover': { bgcolor: 'action.hover' }
                }}
              >
                <Typography variant="body2" color="success.main">성공</Typography>
                <Typography variant="h5" fontWeight="bold" color="success.main">{successCount}</Typography>
              </Paper>
              <Paper
                elevation={statusFilter === 'error' ? 3 : 1}
                onClick={() => setStatusFilter('error')}
                sx={{
                  px: 2, py: 1, cursor: 'pointer',
                  border: statusFilter === 'error' ? '2px solid' : '1px solid',
                  borderColor: statusFilter === 'error' ? 'error.main' : 'divider',
                  '&:hover': { bgcolor: 'action.hover' }
                }}
              >
                <Typography variant="body2" color="error.main">실패</Typography>
                <Typography variant="h5" fontWeight="bold" color="error.main">{errorCount}</Typography>
              </Paper>
              {/* 지점 수 표시 */}
              <Paper elevation={1} sx={{ px: 2, py: 1, bgcolor: 'grey.100' }}>
                <Typography variant="body2" color="text.secondary">지점</Typography>
                <Typography variant="h5" fontWeight="bold">{groupedResults.length}</Typography>
              </Paper>
            </Box>

            {queuedCount > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                백그라운드에서 변환이 진행됩니다. 결과는 BigQuery에서 확인하세요.
              </Alert>
            )}

            {/* 지점별 → 프롬프트별 2단계 그룹화된 결과 */}
            <Box sx={{ maxHeight: `calc(${drawerHeight}vh - 220px)`, overflow: 'auto' }}>
              {groupedResults.map(([branchName, promptGroups]) => {
                // 전체 아이템 가져오기
                const allItems = Object.values(promptGroups).flat();

                // 필터링 적용
                const hasFilteredItems = allItems.some(item => {
                  if (statusFilter === 'all') return true;
                  if (statusFilter === 'success') return item.status === 'success' || item.status === 'validation_failed';
                  if (statusFilter === 'error') return item.status === 'error';
                  if (statusFilter === 'queued') return item.status === 'queued';
                  return true;
                });

                if (!hasFilteredItems) return null;

                const groupSuccessCount = allItems.filter(r => r.status === 'success' || r.status === 'validation_failed').length;
                const groupErrorCount = allItems.filter(r => r.status === 'error').length;
                const groupQueuedCount = allItems.filter(r => r.status === 'queued').length;
                const promptCount = Object.keys(promptGroups).length;
                const isExpanded = expandedGroups[branchName] !== false;

                return (
                  <Accordion
                    key={branchName}
                    expanded={isExpanded}
                    onChange={() => toggleGroup(branchName)}
                    sx={{ mb: 1 }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{
                        bgcolor: groupErrorCount > 0 ? 'error.50' : groupQueuedCount > 0 ? 'info.50' : 'success.50',
                        '&:hover': { bgcolor: groupErrorCount > 0 ? 'error.100' : groupQueuedCount > 0 ? 'info.100' : 'success.100' }
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                        <FolderOpenIcon color={groupErrorCount > 0 ? 'error' : groupQueuedCount > 0 ? 'info' : 'success'} />
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1 }}>
                          {branchName}
                        </Typography>
                        <Chip size="small" label={`${promptCount}개 프롬프트`} variant="outlined" sx={{ mr: 1 }} />
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {groupQueuedCount > 0 && (
                            <Chip size="small" label={`대기 ${groupQueuedCount}`} color="info" variant="filled" />
                          )}
                          {groupSuccessCount > 0 && (
                            <Chip size="small" label={`성공 ${groupSuccessCount}`} color="success" variant="filled" />
                          )}
                          {groupErrorCount > 0 && (
                            <Chip size="small" label={`실패 ${groupErrorCount}`} color="error" variant="filled" />
                          )}
                        </Box>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      {/* 프롬프트별 그룹 */}
                      {Object.entries(promptGroups).map(([promptId, items]) => {
                        // 필터링 적용
                        const filteredItems = items.filter(item => {
                          if (statusFilter === 'all') return true;
                          if (statusFilter === 'success') return item.status === 'success' || item.status === 'validation_failed';
                          if (statusFilter === 'error') return item.status === 'error';
                          if (statusFilter === 'queued') return item.status === 'queued';
                          return true;
                        });

                        if (filteredItems.length === 0) return null;

                        const promptSuccessCount = items.filter(r => r.status === 'success' || r.status === 'validation_failed').length;
                        const promptErrorCount = items.filter(r => r.status === 'error').length;
                        const promptQueuedCount = items.filter(r => r.status === 'queued').length;
                        const promptKey = `${branchName}:${promptId}`;
                        const isPromptExpanded = expandedPrompts[promptKey] || false;

                        return (
                          <Box key={promptId} sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
                            {/* 프롬프트 헤더 (클릭하면 날짜별 상세 펼침) */}
                            <Box
                              onClick={() => togglePrompt(branchName, promptId)}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                px: 2,
                                py: 1,
                                cursor: 'pointer',
                                bgcolor: promptErrorCount > 0 ? 'error.25' : promptQueuedCount > 0 ? 'info.25' : 'grey.50',
                                '&:hover': { bgcolor: 'action.hover' }
                              }}
                            >
                              {isPromptExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                              <CodeIcon fontSize="small" color={promptErrorCount > 0 ? 'error' : promptQueuedCount > 0 ? 'info' : 'success'} />
                              <Typography variant="body2" sx={{ fontWeight: 500, flex: 1 }}>
                                {promptId}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                                {items.length}개 날짜
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                {promptQueuedCount > 0 && (
                                  <Chip size="small" label={promptQueuedCount} color="info" sx={{ height: 20, fontSize: '0.65rem' }} />
                                )}
                                {promptSuccessCount > 0 && (
                                  <Chip size="small" label={promptSuccessCount} color="success" sx={{ height: 20, fontSize: '0.65rem' }} />
                                )}
                                {promptErrorCount > 0 && (
                                  <Chip size="small" label={promptErrorCount} color="error" sx={{ height: 20, fontSize: '0.65rem' }} />
                                )}
                              </Box>
                            </Box>

                            {/* 날짜별 상세 결과 */}
                            <Collapse in={isPromptExpanded}>
                              <List dense sx={{ pl: 2, bgcolor: 'grey.25' }}>
                                {filteredItems.map((item) => (
                                  <ListItem
                                    key={item.originalIndex}
                                    sx={{
                                      borderBottom: '1px solid',
                                      borderColor: 'divider',
                                      py: 1,
                                      flexDirection: 'column',
                                      alignItems: 'flex-start'
                                    }}
                                  >
                                    {/* 상단: 상태 아이콘 + BSDA */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', mb: 0.5 }}>
                                      {item.status === 'queued' ? (
                                        <HourglassEmptyIcon color="info" fontSize="small" />
                                      ) : item.status === 'success' || item.status === 'validation_failed' ? (
                                        <CheckCircleOutlineIcon color="success" fontSize="small" />
                                      ) : (
                                        <ErrorIcon color="error" fontSize="small" />
                                      )}
                                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                        BSDA: {item.bsda}
                                      </Typography>
                                      {item.execution_name && (
                                        <Typography variant="caption" color="primary.main" sx={{ ml: 'auto' }}>
                                          Job: {item.execution_name?.slice(-12)}
                                        </Typography>
                                      )}
                                    </Box>

                                    {/* 성공 결과 */}
                                    {(item.status === 'success' || item.status === 'validation_failed') && (
                                      <Box sx={{ pl: 3, width: '100%' }}>
                                        {item.output_path && (
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="caption" color="success.main" sx={{ flex: 1 }}>
                                              {item.output_path.split('/').pop()}
                                            </Typography>
                                            {item.download_url && (
                                              <Button
                                                variant="outlined"
                                                size="small"
                                                startIcon={<DownloadIcon />}
                                                href={item.download_url}
                                                target="_blank"
                                                sx={{ fontSize: '0.65rem', py: 0.2, minWidth: 'auto' }}
                                              >
                                                다운로드
                                              </Button>
                                            )}
                                          </Box>
                                        )}
                                        {item.bigquery && item.bigquery.rows_inserted !== undefined && (
                                          <Typography variant="caption" color="text.secondary">
                                            BigQuery: {item.bigquery.rows_inserted}행 삽입
                                          </Typography>
                                        )}
                                      </Box>
                                    )}

                                    {/* 대기중 상태 */}
                                    {item.status === 'queued' && (
                                      <Box sx={{ pl: 3 }}>
                                        <Typography variant="caption" color="info.main">
                                          백그라운드에서 변환 중...
                                        </Typography>
                                      </Box>
                                    )}

                                    {/* 오류 표시 */}
                                    {item.status === 'error' && (
                                      <Box sx={{ pl: 3, width: '100%' }}>
                                        <Typography variant="caption" color="error.main">
                                          {item.error}
                                        </Typography>
                                      </Box>
                                    )}

                                    {/* 요청 정보 토글 (디버깅용) */}
                                    {item.requestBody && (
                                      <Box sx={{ pl: 3, width: '100%', mt: 0.5 }}>
                                        <Button
                                          size="small"
                                          startIcon={<CodeIcon />}
                                          onClick={() => toggleRequestBody(item.originalIndex)}
                                          sx={{ fontSize: '0.65rem', py: 0, textTransform: 'none' }}
                                        >
                                          {showRequestBody[item.originalIndex] ? '요청 정보 숨기기' : '요청 정보 보기'}
                                        </Button>
                                        <Collapse in={showRequestBody[item.originalIndex]}>
                                          <Box sx={{ mt: 0.5, p: 1, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                                            <pre style={{ margin: 0, fontSize: '0.6rem', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                                              {JSON.stringify(item.requestBody, null, 2)}
                                            </pre>
                                          </Box>
                                        </Collapse>
                                      </Box>
                                    )}
                                  </ListItem>
                                ))}
                              </List>
                            </Collapse>
                          </Box>
                        );
                      })}
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default BatchResultsDrawer;
